import { Stack, TableCellProps, Typography } from '@mui/material';
import { AttendanceCode } from '@schooly/api';
import { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  GridCell,
  GridContainer,
  GridHeaderCellContent,
  GridRowName,
} from '../../../components/uikit-components/Grid/Grid';
import { Table, TableCell } from '../../../components/uikit-components/Table/Table';
import {
  AttendanceStructureSummary,
  getAttendancePercentages,
  getNoStatusTotal,
  getPresentAbsentCodeIds,
  getTotalByCodeIds,
} from '../attendance-utils';
import { TypographyWithOverflowHint } from 'libs/style/src/components/TypographyWithOverflowHint';

interface AttendanceRegisterPreviewStructureGridProps {
  codes: AttendanceCode[];
  structure: AttendanceStructureSummary;
}

const NUMBER_COL_WIDTH = 165;
const numberCellSx = { width: NUMBER_COL_WIDTH, minWidth: NUMBER_COL_WIDTH };

export const AttendanceRegisterPreviewStructureGrid: FC<
  AttendanceRegisterPreviewStructureGridProps
> = ({ codes, structure }) => {
  const { formatMessage } = useIntl();
  const { present: presentCodeIds, absent: absentCodeIds } = useMemo(
    () => getPresentAbsentCodeIds(codes),
    [codes],
  );

  const columns: TableCell[] = useMemo(
    () => [
      {
        id: 'structure',
        hoverProps: { disableHover: true },
        renderContent: (props: TableCellProps) => (
          <GridCell sticky py={1.5} sx={{ maxWidth: 200 }} {...props}>
            <TypographyWithOverflowHint
              variant="h3"
              color="primary.main"
              noWrap
              sx={{ lineHeight: '18px' }}
            >
              {structure.name}
            </TypographyWithOverflowHint>
          </GridCell>
        ),
      },
      {
        id: 'attendance-Present',
        renderContent: (props: TableCellProps) => (
          <GridCell py={1.5} center sx={numberCellSx} {...props}>
            <GridHeaderCellContent size="normal" color="common.grey">
              <FormattedMessage id="attendance-Present" />
            </GridHeaderCellContent>
          </GridCell>
        ),
      },
      {
        id: 'attendance-Absent',
        renderContent: (props: TableCellProps) => (
          <GridCell py={1.5} center sx={numberCellSx} {...props}>
            <GridHeaderCellContent size="normal" color="common.grey">
              <FormattedMessage id="attendance-Absent" />
            </GridHeaderCellContent>
          </GridCell>
        ),
      },
      {
        id: 'attendance-NoStatus',
        renderContent: (props: TableCellProps) => (
          <GridCell py={1.5} center sx={numberCellSx} {...props}>
            <GridHeaderCellContent size="normal" color="common.grey">
              <FormattedMessage id="attendance-NoStatus" />
            </GridHeaderCellContent>
          </GridCell>
        ),
      },
    ],
    [structure.name],
  );

  const rows = useMemo(() => {
    const entries = [
      ...structure.periods,
      {
        id: `${structure.id}-overall`,
        name: formatMessage({ id: 'attendance-Overall' }),
        attendanceDetails: structure.overall,
      },
    ];

    return entries.map((entry) => {
      const noStatusCount = getNoStatusTotal(entry.attendanceDetails);
      const presentCount = getTotalByCodeIds(presentCodeIds, entry.attendanceDetails);
      const absentCount = getTotalByCodeIds(absentCodeIds, entry.attendanceDetails);
      const { present: presentPercent, absent: absentPercent } = getAttendancePercentages(
        presentCount,
        absentCount,
      );

      const cells: TableCell[] = [
        {
          id: 'periodName',
          renderContent: (props) => (
            <GridCell sticky noVerticalPadding py={1.25} sx={{ maxWidth: 200 }} {...props}>
              <GridRowName>
                <TypographyWithOverflowHint variant="h3" noWrap>
                  {entry.name}
                </TypographyWithOverflowHint>
              </GridRowName>
            </GridCell>
          ),
        },
        {
          id: 'attendance-Present',
          renderContent: (props: TableCellProps) => (
            <GridCell py={1.5} noHorizontalPadding center {...props}>
              <Stack direction="row" justifyContent="center" alignItems="center" width="100%">
                {presentCount ? (
                  <Typography color="text.primary">{presentPercent}%</Typography>
                ) : (
                  <Typography color="text.secondary">-</Typography>
                )}
              </Stack>
            </GridCell>
          ),
        },
        {
          id: 'attendance-Absent',
          renderContent: (props: TableCellProps) => (
            <GridCell py={1.5} noHorizontalPadding center {...props}>
              <Stack direction="row" justifyContent="center" alignItems="center" width="100%">
                {absentCount ? (
                  <Typography color="text.primary">{absentPercent}%</Typography>
                ) : (
                  <Typography color="text.secondary">-</Typography>
                )}
              </Stack>
            </GridCell>
          ),
        },
        {
          id: 'attendance-NoStatus',
          renderContent: (props: TableCellProps) => (
            <GridCell py={1.5} noHorizontalPadding center {...props}>
              <Stack direction="row" justifyContent="center" alignItems="center" width="100%">
                {noStatusCount ? (
                  <Typography color="text.primary">{noStatusCount}</Typography>
                ) : (
                  <Typography color="text.secondary">-</Typography>
                )}
              </Stack>
            </GridCell>
          ),
        },
      ];

      return { id: entry.id, cells };
    });
  }, [absentCodeIds, formatMessage, presentCodeIds, structure]);

  return (
    <GridContainer
      sx={(theme) => ({
        overflow: 'clip',
        flexShrink: 0,
        '& tr:last-child td': { borderTop: theme.mixins.borderValue() },
      })}
    >
      <Table rows={rows} columns={columns} />
    </GridContainer>
  );
};
